import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Assignment,
  DoneAll,
  Schedule,
  Clear,
  Shuffle,
  Block,
  AddCircleOutline,
} from "@mui/icons-material";

import order_icon from "../../assets/icons/orders.svg";
import clock_loader_10 from "../../assets/icons/clock_loader_10.svg";
import inventory_icon from "../../assets/icons/inventory.svg";
import deployed_code_history from "../../assets/icons/deployed_code_history.svg";
import box_add from "../../assets/icons/box_add.svg";
import remove_circle_outline from "../../assets/icons/remove_circle_outline.svg";

// Components
import SearchComponent from "../../components/Search";
import PaginationComponentOne from "../../components/Pagination/Pagination-O";

// Redux actions
import { getAllStockRequests } from "../../redux/StoreManagement/storeManagementActions";

const StockRequestsTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state
  const {
    stockRequestsData,
    stockRequestsLoading,
    stockRequestsError,
    stockRequestsTotalCount,
    stockRequestsCurrentPage,
  } = useSelector((state) => state.storeManagement);

  // Local state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState("");
  const [activeTab, setActiveTab] = useState("All");

  // Tab configuration - Added "All" tab
  const tabs = [
    {
      id: "All",
      label: "All",
      count: activeTab === "All" ? stockRequestsTotalCount || 0 : 0,
      bgColor: "bg-[#E6F3FF]",
      textColor: "text-[#0066CC]",
    },
    {
      id: "PENDING",
      label: "Pending",
      count: activeTab === "PENDING" ? stockRequestsTotalCount || 0 : 0,
      bgColor: "bg-[#FFF7E6]",
      textColor: "text-[#D4A407]",
    },
    {
      id: "PROCESSING",
      label: "Processing",
      count: activeTab === "PROCESSING" ? stockRequestsTotalCount || 0 : 0,
      bgColor: "bg-[#FFF7E6]",
      textColor: "text-[#D4A407]",
    },
    {
      id: "APPROVED",
      label: "Approved",
      count: activeTab === "APPROVED" ? stockRequestsTotalCount || 0 : 0,
      bgColor: "bg-[#DBFFDF]",
      textColor: "text-[#2B8C34]",
    },
    {
      id: "Dispatched",
      label: "Dispatched",
      count: activeTab === "Dispatched" ? stockRequestsTotalCount || 0 : 0,
      bgColor: "bg-[#E6F3FF]",
      textColor: "text-[#0066CC]",
    },
    {
      id: "Confirmed",
      label: "Fulfilled",
      count: activeTab === "Confirmed" ? stockRequestsTotalCount || 0 : 0,
      bgColor: "bg-[#DBFFDF]",
      textColor: "text-[#2B8C34]",
    },
    {
      id: "Rejected",
      label: "Rejected",
      count: activeTab === "Rejected" ? stockRequestsTotalCount || 0 : 0,
      bgColor: "bg-[#FFEBEE]",
      textColor: "text-[#B92020]",
    },
  ];

  // Use data directly from Redux (no frontend filtering needed)
  const displayData = useMemo(() => {
    if (!stockRequestsData || !Array.isArray(stockRequestsData)) return [];
    return stockRequestsData;
  }, [stockRequestsData]);

  // Handle search
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    setCurrentPage(1);
    dispatch(
      getAllStockRequests({
        page: 1,
        page_size: pageSize,
        search: value,
        status: activeTab,
      })
    );
  };

  // Handle pagination
  const handlePageChange = (paginationData) => {
    if (typeof paginationData === "object") {
      if (paginationData.page !== undefined) {
        setCurrentPage(paginationData.page);
      }
      if (
        paginationData.pageSize !== undefined &&
        paginationData.pageSize !== pageSize
      ) {
        setPageSize(paginationData.pageSize);
        setCurrentPage(1);
      }

      // Dispatch API call with the new values
      dispatch(
        getAllStockRequests({
          page:
            paginationData.pageSize !== undefined &&
            paginationData.pageSize !== pageSize
              ? 1
              : paginationData.page,
          page_size: paginationData.pageSize || pageSize,
          search: searchValue,
          status: activeTab,
        })
      );
    } else {
      setCurrentPage(paginationData);
      dispatch(
        getAllStockRequests({
          page: paginationData,
          page_size: pageSize,
          search: searchValue,
          status: activeTab,
        })
      );
    }
  };

  // Handle tab change
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setCurrentPage(1);
    setSearchValue("");
    dispatch(
      getAllStockRequests({
        page: 1,
        page_size: pageSize,
        search: "",
        status: tabId,
      })
    );
  };

  // Handle actions - use internal_id for backend operations
  const handleViewRequest = (request) => {
    // Use internal_id if you need it for backend calls, or id for URL
    navigate(
      `/store-management/stock-requests/${request.internal_id || request.id}`
    );
  };

  // Get approval status icon
  const getApprovalStatusIcon = (status) => {
    switch (status) {
      case "APPROVED":
        return (
          <DoneAll
            className="text-[#2B8C34]"
            style={{ width: "12px", height: "12px" }}
          />
        );

      case "PART_APPROVED":
        return (
          <img
            src={remove_circle_outline}
            alt=""
            style={{ width: "12px", height: "12px" }}
          />
        );

      case "OVER_APPROVED":
        return (
          <AddCircleOutline
            className="text-[#0F766E]"
            style={{ width: "12px", height: "12px" }}
          />
        );
      case "PENDING":
        return (
          <Schedule
            className="text-[#D4A407]"
            style={{ width: "12px", height: "12px" }}
          />
        );
      case "REJECTED":
        return (
          <Block
            className="text-[#B92020]"
            style={{ width: "12px", height: "12px" }}
          />
        );
      default:
        return (
          <img
            src={order_icon}
            alt="Order Icon"
            style={{ width: "12px", height: "12px" }}
          />
        );
    }
  };

  const formatApprovalStatus = (status) => {
    switch (status) {
      case "":
        return "No Item";
      case "APPROVED":
        return "Approved";
      case "PART_APPROVED":
        return "Part Approved";
      case "OVER_APPROVED":
        return "Over Approved";
      case "PENDING":
        return "Pending";
      case "REJECTED":
        return "Rejected";
      default:
        return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    }
  };

  // Get approval status badge style
  const getApprovalStatusBadge = (status) => {
    switch (status) {
      case "APPROVED":
        return "bg-[#E1F7E3] text-[#24752B] border border-[#72B278]";
      case "OVER_APPROVED":
        return "bg-[#FFFFFF] text-[#24752B] border border-[#0F766E]";
      case "PART_APPROVED":
        return "bg-[#FFFFFF] text-[#C2410C] border border-[#C2410C]";
      case "PENDING":
        return "bg-[#FFFFFF] text-[#E29700] border border-[#E29700]";
      case "REJECTED":
        return "bg-[#FEF0EC] text-[#B92020] border border-[#E75E63]";
      default:
        return "bg-[#ECEEEC] text-[#96A397] border border-[#96A397]";
    }
  };

  // Get fulfillment status badge style
  const getFulfillmentStatusBadge = (status) => {
    switch (status) {
      case "PROCESSING":
        return "bg-[#FFFCC5] text-[#D4A407] border border-[#FFCF31]";
      case "DISPATCHED":
        return "bg-[#FFFFFF] text-[#0066AF] border border-[#0066AF]";
      case "FULFILLED":
        return "bg-[#E1F7E3] text-[#2B8C34] border border-[#2B8C34]";
      case "CONFIRMED":
        return "bg-[#E1F7E3] text-[#2B8C34] border border-[#2B8C34]";
      case "CANCELLED":
        return "bg-[#FEF0EC] text-[#B92020] border border-[#E75E63]";
      case "PENDING":
        return "bg-[#FFFFFF] text-[#D4A407] border border-[#D4A407]";
      case "PART_FULFILLED":
        return "bg-[#FFEDD5] text-[#C2410C] border border-[#F97316]";
      case "PART_DELIVERY":
        return "bg-[#FFEDD5] text-[#C2410C] border border-[#F97316]";
      case "OVER_FULFILLED":
        return "bg-[#CCFBF1] text-[#0F766E] border border-[#14B8A6]";
      default:
        return "bg-[#ECEEEC] text-[#96A397] border border-[#96A397]";
    }
  };

  const formatFulfillmentStatus = (status) => {
    switch (status) {
      case "--":
        return "No Item";
      case "PROCESSING":
        return "Processing";
      case "DISPATCHED":
        return "Dispatched";
      case "CONFIRMED":
        return "Confirmed";
      case "CANCELLED":
        return "Cancelled";
      case "PENDING":
        return "Pending";
      case "PART_FULFILLED":
        return "Part Fulfilled";
      case "PART_DELIVERY":
        return "Part Delivery";
      case "OVER_FULFILLED":
        return "Over Fulfilled";
      default:
        return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    }
  };

  // Get fulfillment status icon
  const getFulfillmentStatusIcon = (status) => {
    switch (status) {
      case "PROCESSING":
        return (
          <img
            src={deployed_code_history}
            alt="Processing Icon"
            style={{ width: "12px", height: "12px" }}
          />
        );
      case "DISPATCHED":
        return (
          <Shuffle
            className="text-[#0066AF]"
            style={{ width: "12px", height: "12px" }}
          />
        );
      case "CONFIRMED":
        return (
          <DoneAll
            className="text-[#2B8C34]"
            style={{ width: "12px", height: "12px" }}
          />
        );
      case "FULFILLED":
        return (
          <img
            src={inventory_icon}
            alt="Fulfilled Icon"
            className="text-[#2B8C34]"
            style={{ width: "12px", height: "12px" }}
          />
        );
      case "CANCELLED":
        return (
          <Clear
            className="text-[#B92020]"
            style={{ width: "12px", height: "12px" }}
          />
        );
      case "PENDING":
        return (
          <Schedule
            className="text-[#D4A407]"
            style={{ width: "12px", height: "12px" }}
          />
        );
      case "PART_FULFILLED":
        return (
          <img
            src={clock_loader_10}
            alt="Part Fulfilled Icon"
            style={{ width: "12px", height: "12px" }}
          />
        );
      case "PART_DELIVERY":
        return (
          <img
            src={clock_loader_10}
            alt="Part Delivery Icon"
            style={{ width: "12px", height: "12px" }}
          />
        );
      case "OVER_FULFILLED":
        return (
          <img
            src={box_add}
            alt="Over Fulfilled Icon"
            style={{ width: "12px", height: "12px" }}
          />
        );
      default:
        return (
          <img
            src={order_icon}
            alt="Order Icon"
            style={{ width: "12px", height: "12px" }}
          />
        );
    }
  };

  // Fetch data on component mount - Changed to "All"
  useEffect(() => {
    dispatch(
      getAllStockRequests({
        page: 1,
        page_size: pageSize,
        search: "",
        status: "All",
      })
    );
  }, [dispatch, pageSize]);

  return (
    <div className="bg-white rounded-lg border border-[#E1E6E1]">
      {/* Header */}
      <div className="p-4 border-b border-[#E1E6E1]">
        <div className="flex flex-col gap-4 mb-4">
          <h3 className="text-lg font-semibold text-[#4B564D]">Request List</h3>

          {/* Search */}
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <SearchComponent
                searchValue={searchValue}
                handleChange={handleSearch}
                placeholder="Search requests..."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4 pt-4">
        <div className="flex items-center border border-[#E1E6E1] bg-[#F6F7F6] rounded-lg overflow-hidden w-fit">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`px-3 py-2 text-center text-sm font-semibold flex items-center justify-center gap-2 ${
                activeTab === tab.id ? "bg-white rounded-lg" : "bg-[#F6F7F6]"
              }`}
            >
              <span
                className={
                  activeTab === tab.id ? "text-[#161D17]" : "text-[#5C715E]"
                }
              >
                {tab.label}
              </span>
              <span
                className={`px-2 py-1 rounded-[4px] text-xs font-medium ${
                  activeTab === tab.id
                    ? tab.bgColor + " " + tab.textColor
                    : "bg-[#E1E6E1] text-[#5C715E]"
                }`}
              >
                {activeTab === tab.id && stockRequestsLoading ? (
                  <span className="inline-block w-4 h-4 border-2 border-[#96A397] border-t-transparent rounded-full animate-spin"></span>
                ) : (
                  tab.count
                )}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Table Content */}
      <div className="overflow-x-auto relative py-4">
        {stockRequestsLoading ? (
          <div className="animate-pulse w-full px-4 mb-4">
            {/* Loading shimmer */}
            <div className="h-12 bg-gray-200 rounded-lg w-full mb-2"></div>
            <div className="h-10 bg-gray-200 rounded-lg w-full mb-2"></div>
            <div className="h-10 bg-gray-200 rounded-lg w-full mb-2"></div>
            <div className="h-10 bg-gray-200 rounded-lg w-full mb-2"></div>
            <div className="h-10 bg-gray-200 rounded-lg w-full mb-2"></div>
          </div>
        ) : stockRequestsError ? (
          <div className="p-8 text-center">
            <div className="text-red-600 mb-2">
              Error loading stock requests
            </div>
            <div className="text-sm text-gray-500 mb-4">
              {stockRequestsError}
            </div>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
            >
              Try Again
            </button>
          </div>
        ) : !displayData || displayData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-4">
            <div className="w-[48px] flex justify-center items-center h-[48px] rounded-full border border-[#E1E6E1] bg-[#F6F7F6] mb-4">
              <Assignment className="text-[#5F6D60] w-6 h-6" />
            </div>
            <p className="text-sm font-semibold text-[#5F6D60] text-center">
              {searchValue
                ? "No requests match your search criteria."
                : "No stock requests found."}
            </p>
          </div>
        ) : (
          <>
            {/* Table */}
            <table
              className="w-full text-left rounded-lg overflow-hidden border border-[#E1E6E1] mx-4"
              style={{ width: "calc(100% - 2rem)" }}
            >
              <thead className="bg-[#F6F7F6] border-[#E1E6E1]">
                <tr className="text-sm">
                  <th className="px-4 py-4 font-semibold text-[#5F6D60] rounded-tl-lg rounded-bl-lg">
                    Request ID
                  </th>
                  <th className="px-4 py-4 font-semibold text-[#5F6D60]">
                    Date Requested
                  </th>
                  <th className="px-4 py-4 font-semibold text-[#5F6D60]">
                    Requested By
                  </th>
                  <th className="px-4 py-4 font-semibold text-[#5F6D60]">
                    Products
                  </th>
                  <th className="px-4 py-4 font-semibold text-[#5F6D60]">
                    Qty
                  </th>
                  <th className="px-4 py-4 font-semibold text-[#5F6D60]">
                    Approval Status
                  </th>
                  <th className="px-4 py-4 font-semibold text-[#5F6D60] rounded-tr-lg rounded-br-lg">
                    Fulfillment Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white border-[#E1E6E1]">
                {displayData.map((request) => (
                  <tr
                    key={request.internal_id || request.id}
                    className="text-sm cursor-pointer hover:bg-[#EDF7EE] border-b border-[#E1E6E1]"
                    onClick={() => handleViewRequest(request)}
                  >
                    <td className="px-4 py-4 font-semibold text-[#5F6D60]">
                      {request.id}
                    </td>
                    <td className="px-4 py-4 text-[#5F6D60] font-normal">
                      {request.date_requested}
                    </td>
                    <td className="px-4 py-4 text-[#5F6D60] font-semibold">
                      {request.requested_by}
                    </td>
                    <td className="px-4 py-4 text-[#5F6D60] font-semibold">
                      <div className="flex items-center gap-1">
                        <p className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-md font-semibold whitespace-nowrap">
                          {request.product_name}
                        </p>
                        {request.variant_data?.type !== "N/A" && (
                          <>
                            <span className="font-semibold text-lg">/</span>
                            <p className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-md font-semibold">
                              {request.variant_data.type}
                            </p>
                          </>
                        )}
                        {request.variant_data?.value_unit !== "N/A" && (
                          <>
                            <span className="font-semibold text-lg">/</span>
                            <p className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-md font-semibold">
                              {request.variant_data.value_unit}
                            </p>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-[#5F6D60] font-semibold">
                      <p className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-md font-medium">
                        {request.quantity}
                      </p>
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex gap-1 whitespace-nowrap items-center px-2 py-1 text-xs font-semibold rounded-[4px] ${getApprovalStatusBadge(
                          request.approval_status
                        )}`}
                      >
                        {getApprovalStatusIcon(request.approval_status)}{" "}
                        {formatApprovalStatus(request.approval_status)}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex gap-1 items-center px-2 py-1 text-xs font-semibold rounded-[4px] ${getFulfillmentStatusBadge(
                          request.fulfillment_status
                        )}`}
                      >
                        {getFulfillmentStatusIcon(request.fulfillment_status)}{" "}
                        {formatFulfillmentStatus(request.fulfillment_status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="border-t border-[#E1E6E1]">
              <PaginationComponentOne
                page={stockRequestsCurrentPage || currentPage}
                totalItems={stockRequestsTotalCount || 0}
                pageSize={pageSize}
                onPageChange={handlePageChange}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StockRequestsTable;
